import { mergeClasses } from '@fluentui/react-components'
import { useRef } from 'react'
import { useContextListMeasurements } from '../../hooks/useContextListMeasurements'
import { ContextCard } from '../ContextCard/ContextCard'
import { useStyles } from './ContextListFrame.styles'

interface ContextListFrameProps {
  /** Whether the list should currently claim any space at all - false while collapsed or while
   *  imatch is running (it temporarily takes over the whole context bar). */
  isVisible: boolean
  /** The list's resolved height for this render - already the final answer, not a cap to fit
   *  content within. ChatInput computes this (via allocateComposerHeights); this component just
   *  renders it. */
  allocatedHeight: number
  /** Instant sizing belongs only to a drag tracking the pointer. Closing is a state change, not
   *  tracking, so it should glide shut on its own transition instead. */
  animateResize: boolean
  ariaLabel: string
  items: string[]
  onContentHeightChange?: (height: number) => void
}

/**
 * The context list's sized, scrollable viewport: a fixed-height frame (set by ChatInput's
 * composer allocator) containing a scrolling list of ContextCards with fade indicators at the
 * ends. Purely presentational - all sizing math and content-height measurement live in
 * useContextListMeasurements and the caller's allocator.
 */
const ContextListFrame = ({
  isVisible,
  allocatedHeight,
  animateResize,
  ariaLabel,
  items,
  onContentHeightChange,
}: ContextListFrameProps) => {
  const classes = useStyles()
  const listRef = useRef<HTMLDivElement>(null)

  const { canScrollUp, canScrollDown, onScroll } = useContextListMeasurements(
    listRef,
    items.length,
    onContentHeightChange
  )

  return (
    <div
      data-testid="context-list-frame"
      className={mergeClasses(
        classes.listFrame,
        animateResize ? classes.listFrameAnimated : classes.listFrameInstant
      )}
      style={
        {
          // Desired list height as a flex-basis; collapse drives it to 0.
          '--context-list-target-height': isVisible ? `${allocatedHeight}px` : '0px',
        } as React.CSSProperties
      }>
      <div className={classes.listContainer}>
        <div
          data-testid="context-scroll-fade-top"
          data-visible={canScrollUp ? 'true' : 'false'}
          className={mergeClasses(
            classes.scrollFade,
            classes.scrollFadeTop,
            canScrollUp && classes.scrollFadeVisible
          )}
        />
        <div
          data-testid="context-scroll-fade-bottom"
          data-visible={canScrollDown ? 'true' : 'false'}
          className={mergeClasses(
            classes.scrollFade,
            classes.scrollFadeBottom,
            canScrollDown && classes.scrollFadeVisible
          )}
        />

        <div
          ref={listRef}
          role="list"
          aria-label={ariaLabel}
          className={classes.list}
          onScroll={onScroll}>
          {items.map((id) => (
            <ContextCard key={id} id={id} />
          ))}
        </div>
      </div>
    </div>
  )
}

export { ContextListFrame }
